import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, BookOpen, AlertCircle } from "lucide-react";
import api from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/blogs/${slug}`)
      .then((r) => {
        setPost(r.data);
        // Update document title for SEO
        document.title = `${r.data.title} — One Light Stays`;
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));

    return () => { document.title = "One Light Stays"; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-stone-400 text-sm animate-pulse">Loading article…</div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <AlertCircle className="w-12 h-12 text-stone-300 mb-4" strokeWidth={1.5} />
        <h1 className="font-display text-2xl mb-2">Article not found</h1>
        <p className="text-stone-500 text-sm mb-6">
          This blog post may have been removed or the URL is incorrect.
        </p>
        <Link to="/" className="btn-primary">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <article className="bg-white" data-testid="blog-post-page">
      {/* ── HERO IMAGE ────────────────────────────────────────── */}
      {post.featured_image && (
        <div className="relative h-[50vh] md:h-[60vh] min-h-[320px] overflow-hidden bg-stone-100">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          {/* Title overlay on image */}
          <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-6 lg:px-10 pb-10">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-3">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(post.created_at)}</span>
            </div>
            <h1 className="font-display text-3xl md:text-5xl text-white leading-tight">
              {post.title}
            </h1>
          </div>
        </div>
      )}

      {/* ── CONTENT ───────────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12 md:py-16">
        {/* Back link + meta (shown above content when no featured image) */}
        {!post.featured_image && (
          <div className="mb-8">
            <div className="flex items-center gap-2 text-stone-400 text-sm mb-6">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(post.created_at)}</span>
            </div>
            <h1 className="font-display text-3xl md:text-5xl leading-tight mb-0">
              {post.title}
            </h1>
          </div>
        )}

        {/* Back button */}
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-stone-500 hover:text-stone-900 text-sm font-medium mb-10 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        {/* ── Rich text content ── */}
        <div
          className="blog-content prose-ols"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Updated date */}
        {post.updated_at && post.updated_at !== post.created_at && (
          <div className="mt-12 pt-6 border-t border-stone-100 text-xs text-stone-400">
            Last updated {formatDate(post.updated_at)}
          </div>
        )}
      </div>

      {/* ── RELATED CTA ───────────────────────────────────────── */}
      <div className="bg-stone-50 border-t border-stone-100 py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <BookOpen className="w-8 h-8 mx-auto text-stone-400 mb-3" strokeWidth={1.5} />
          <div className="font-display text-xl mb-1">Enjoyed this article?</div>
          <p className="text-stone-500 text-sm mb-6">
            Explore our curated stays and plan your perfect getaway.
          </p>
          <Link to="/search" className="btn-primary">
            Explore Stays
          </Link>
        </div>
      </div>
    </article>
  );
}
